// @ts-nocheck - Legacy file, will be refactored

import {
	Alert,
	AlertDescription,
	Badge,
	Button,
	Card,
	CardContent,
	Input,
	Label,
} from "@ancatag/at-design";
import {
	AlertCircle,
	CheckCircle2,
	GripVertical,
	Plus,
	Radio,
	Trash2,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
	type ModelConfig,
	modelConfigsService,
} from "../../../services/models";
import type { GenerationType } from "../../../services/provider-templates";

interface ModelConfigWithPriority {
	modelConfig: ModelConfig;
	isPrimary: boolean;
	priority: number;
}

interface Step3SelectModelsProps {
	generationType: GenerationType;
	selectedModelConfigs: ModelConfigWithPriority[];
	onModelConfigsChange: (modelConfigs: ModelConfigWithPriority[]) => void;
}

export function Step3SelectModels({
	generationType,
	selectedModelConfigs,
	onModelConfigsChange,
}: Step3SelectModelsProps) {
	const [, _navigate] = useLocation();
	const [availableModelConfigs, setAvailableModelConfigs] = useState<
		ModelConfig[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");

	const fetchModels = useCallback(async () => {
		try {
			setLoading(true);
			const response = await modelConfigsService.list({
				generationType,
				isActive: true,
				limit: 100,
			});
			setAvailableModelConfigs(response.data);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to load models");
		} finally {
			setLoading(false);
		}
	}, [generationType]);

	useEffect(() => {
		fetchModels();
	}, [fetchModels]);

	// Refresh models when window regains focus (user might have created a model in another tab)
	useEffect(() => {
		const handleFocus = () => {
			fetchModels();
		};
		window.addEventListener("focus", handleFocus);
		return () => window.removeEventListener("focus", handleFocus);
	}, [fetchModels]);

	const filteredModelConfigs = availableModelConfigs.filter(
		(mc) =>
			!selectedModelConfigs.some((smc) => smc.modelConfig.id === mc.id) &&
			(search === "" ||
				mc.name.toLowerCase().includes(search.toLowerCase()) ||
				mc.modelIdentifier.toLowerCase().includes(search.toLowerCase())),
	);

	const handleAddModelConfig = (modelConfig: ModelConfig) => {
		const isFirst = selectedModelConfigs.length === 0;
		const newModelConfig: ModelConfigWithPriority = {
			modelConfig,
			isPrimary: isFirst,
			priority: selectedModelConfigs.length,
		};
		onModelConfigsChange([...selectedModelConfigs, newModelConfig]);
	};

	const handleRemoveModelConfig = (modelConfigId: string) => {
		const updated = selectedModelConfigs.filter(
			(smc) => smc.modelConfig.id !== modelConfigId,
		);
		// If we removed the primary, make the first one primary
		if (
			updated.length > 0 &&
			selectedModelConfigs.find((smc) => smc.modelConfig.id === modelConfigId)
				?.isPrimary
		) {
			updated[0].isPrimary = true;
		}
		// Recalculate priorities
		updated.forEach((smc, index) => {
			smc.priority = index;
		});
		onModelConfigsChange(updated);
	};

	const handleSetPrimary = (modelConfigId: string) => {
		const updated = selectedModelConfigs.map((smc) => ({
			...smc,
			isPrimary: smc.modelConfig.id === modelConfigId,
		}));
		onModelConfigsChange(updated);
	};

	const handlePriorityChange = (modelConfigId: string, newPriority: number) => {
		const updated = [...selectedModelConfigs];
		const modelConfigIndex = updated.findIndex(
			(smc) => smc.modelConfig.id === modelConfigId,
		);
		if (modelConfigIndex === -1) return;

		const oldPriority = updated[modelConfigIndex].priority;
		updated[modelConfigIndex].priority = newPriority;

		// Swap priorities
		const otherModelConfig = updated.find(
			(smc) =>
				smc.priority === newPriority && smc.modelConfig.id !== modelConfigId,
		);
		if (otherModelConfig) {
			otherModelConfig.priority = oldPriority;
		}

		// Sort by priority
		updated.sort((a, b) => a.priority - b.priority);
		onModelConfigsChange(updated);
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-semibold mb-2">Select Model Configs</h3>
				<p className="text-sm text-muted-foreground mb-4">
					Add model configs to this route configuration. One model config will
					be primary (used for health checks), others will be used as fallbacks
					based on priority.
				</p>
			</div>

			{/* Selected Model Configs */}
			{selectedModelConfigs.length > 0 && (
				<div className="space-y-3">
					<Label>Selected Model Configs ({selectedModelConfigs.length})</Label>
					{selectedModelConfigs
						.sort((a, b) => a.priority - b.priority)
						.map((smc) => (
							<Card key={smc.modelConfig.id} className="relative">
								<CardContent className="pt-4">
									<div className="flex items-start gap-4">
										<div className="flex items-center gap-2 pt-1">
											<GripVertical className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm font-medium text-muted-foreground">
												Priority: {smc.priority}
											</span>
										</div>
										<div className="flex-1">
											<div className="flex items-center gap-2 mb-2">
												<h4 className="font-semibold">
													{smc.modelConfig.name}
												</h4>
												{smc.isPrimary && (
													<Badge variant="default" className="text-xs">
														Primary
													</Badge>
												)}
												<Badge
													variant="outline"
													className={`text-xs ${
														smc.modelConfig.connectionStatus === "connected"
															? "border-green-500 text-green-400"
															: smc.modelConfig.connectionStatus === "error"
																? "border-red-500 text-red-400"
																: "border-yellow-500 text-yellow-400"
													}`}
												>
													{smc.modelConfig.connectionStatus}
												</Badge>
											</div>
											<p className="text-sm text-muted-foreground font-mono mb-2">
												{smc.modelConfig.modelIdentifier}
											</p>
											<div className="flex items-center gap-4">
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleSetPrimary(smc.modelConfig.id)}
													disabled={smc.isPrimary}
												>
													<Radio className="h-3 w-3 mr-1" />
													{smc.isPrimary ? "Primary" : "Set as Primary"}
												</Button>
												<div className="flex items-center gap-2">
													<Label
														htmlFor={`priority-${smc.modelConfig.id}`}
														className="text-xs"
													>
														Priority:
													</Label>
													<Input
														id={`priority-${smc.modelConfig.id}`}
														type="number"
														min="0"
														max={selectedModelConfigs.length - 1}
														value={smc.priority}
														onChange={(e) =>
															handlePriorityChange(
																smc.modelConfig.id,
																parseInt(e.target.value, 10) || 0,
															)
														}
														className="w-20 h-8"
													/>
												</div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() =>
														handleRemoveModelConfig(smc.modelConfig.id)
													}
													className="text-destructive hover:text-destructive"
												>
													<Trash2 className="h-3 w-3 mr-1" />
													Remove
												</Button>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						))}
				</div>
			)}

			{/* Add Model Configs */}
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<Label>Available Model Configs</Label>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							// Open in new tab to preserve route config wizard state
							window.open("/models/manage/new", "_blank");
						}}
					>
						<Plus className="h-4 w-4 mr-2" />
						Create New Model Config
					</Button>
				</div>

				<Input
					placeholder="Search model configs..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="max-w-sm"
				/>

				{loading ? (
					<div className="text-sm text-muted-foreground">
						Loading model configs...
					</div>
				) : filteredModelConfigs.length === 0 ? (
					<Alert>
						<AlertDescription>
							{search
								? "No model configs match your search."
								: selectedModelConfigs.length === 0
									? "No model configs available. Create a model config first."
									: "All available model configs have been added."}
						</AlertDescription>
					</Alert>
				) : (
					<div className="space-y-2 max-h-64 overflow-y-auto">
						{filteredModelConfigs.map((modelConfig) => (
							<Card
								key={modelConfig.id}
								className="cursor-pointer hover:border-primary transition-colors"
								onClick={() => handleAddModelConfig(modelConfig)}
							>
								<CardContent className="pt-4">
									<div className="flex items-center justify-between">
										<div>
											<h4 className="font-semibold">{modelConfig.name}</h4>
											<p className="text-sm text-muted-foreground font-mono">
												{modelConfig.modelIdentifier}
											</p>
										</div>
										<div className="flex items-center gap-2">
											<Badge
												variant="outline"
												className={`text-xs ${
													modelConfig.connectionStatus === "connected"
														? "border-green-500 text-green-400"
														: modelConfig.connectionStatus === "error"
															? "border-red-500 text-red-400"
															: "border-yellow-500 text-yellow-400"
												}`}
											>
												{modelConfig.connectionStatus === "connected" ? (
													<CheckCircle2 className="h-3 w-3 mr-1" />
												) : modelConfig.connectionStatus === "error" ? (
													<XCircle className="h-3 w-3 mr-1" />
												) : (
													<AlertCircle className="h-3 w-3 mr-1" />
												)}
												{modelConfig.connectionStatus}
											</Badge>
											<Button variant="ghost" size="sm">
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</div>

			{selectedModelConfigs.length === 0 && (
				<Alert>
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						You must add at least one model config to this route configuration.
						The first model config added will be set as primary.
					</AlertDescription>
				</Alert>
			)}
		</div>
	);
}
